/**
 * batch-69 samenstellen (FO-509 / FO-510 / FO-516) - de samenstel-opdracht met
 * de scan-afvinklijst: gedeelde request-schema's (API valideert ermee, de
 * formulieren client-side) en de DTO's voor backoffice en portal.
 *
 * De pure regels (status, compleet-regel, scanbeslissing, diff, risico) staan in
 * `@streep/utils` (`samenstelling.ts`). Geen kostprijs of interne prijs in de
 * DTO's: de afvinklijst gaat ook naar de portal.
 */
import { z } from 'zod'
import { idParam } from './id-validators'
import { queryBoolean } from './zod-query'
import type { PakketType } from './gereedschapspakket'
import type { DragerReeks, DragerSetStatus } from './karren'
import { MiddelFotoIdSchema } from './middelen'

// ─── Enums (spiegel van Prisma) ──────────────────────────────────────────────

export const SAMENSTELLING_STATUSSEN = ['WACHT_OP_LEVERING', 'KLAAR', 'UITGEGEVEN', 'GEANNULEERD'] as const
export type SamenstellingStatusDTO = (typeof SAMENSTELLING_STATUSSEN)[number]

export const SAMENSTELLING_STATUS_LABELS: Record<SamenstellingStatusDTO, string> = {
  WACHT_OP_LEVERING: 'Wacht op levering',
  KLAAR: 'Compleet, klaar om samen te stellen',
  UITGEGEVEN: 'Uitgegeven',
  GEANNULEERD: 'Geannuleerd',
}

export const SAMENSTELLING_DOELEN = ['MEDEWERKER', 'DRAGER'] as const
export type SamenstellingDoelDTO = (typeof SAMENSTELLING_DOELEN)[number]

export const SAMENSTEL_REGEL_DELEN = ['GEREEDSCHAP', 'KLEDING', 'SET', 'WELKOMST'] as const
export type SamenstelRegelDeelDTO = (typeof SAMENSTEL_REGEL_DELEN)[number]

export const SAMENSTEL_REGEL_DEEL_LABELS: Record<SamenstelRegelDeelDTO, string> = {
  GEREEDSCHAP: 'Gereedschap',
  KLEDING: 'Kleding',
  SET: 'Complete set',
  WELKOMST: 'Welkomstpakket',
}

export const SAMENSTEL_REGEL_STATUSSEN = [
  'GEBLOKKEERD',
  'WACHT_OP_GOEDKEURING',
  'TE_BESTELLEN',
  'BESTELD',
  'IN_MAGAZIJN',
  'GESCAND',
  'UITGEGEVEN',
  'VOLGT_NA',
  'VERVALLEN',
] as const
export type SamenstelRegelStatusDTO = (typeof SAMENSTEL_REGEL_STATUSSEN)[number]

export const SAMENSTEL_REGEL_STATUS_LABELS: Record<SamenstelRegelStatusDTO, string> = {
  GEBLOKKEERD: 'Geblokkeerd',
  WACHT_OP_GOEDKEURING: 'Wacht op goedkeuring',
  TE_BESTELLEN: 'Te bestellen',
  BESTELD: 'Besteld',
  IN_MAGAZIJN: 'In magazijn, afvinken',
  GESCAND: 'Afgevinkt',
  UITGEGEVEN: 'Uitgegeven',
  VOLGT_NA: 'Volgt na',
  VERVALLEN: 'Vervallen',
}

export type SamenstelRegelKleurDTO = 'GROEN' | 'ORANJE' | 'ROOD' | 'GRIJS'
export type SamenstelRisicoDTO = 'GEEN' | 'RISICO' | 'TE_LAAT'

// ─── Request-schema's ────────────────────────────────────────────────────────

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum als jjjj-mm-dd')
const reden = (min = 3) =>
  z.string().trim().min(min, `Geef een reden (minimaal ${min} tekens)`).max(1000)
const handtekening = z
  .string()
  .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Handtekening ontbreekt of is ongeldig')
  .max(2_000_000)

/** Opdracht aanmaken voor een (nieuwe) medewerker. */
export const SamenstellingAanmakenSchema = z.object({
  employeeId: idParam('employeeId'),
  onboardingId: idParam('onboardingId').nullable().optional(),
  /** Leeg = het pakket dat bij de medewerker hoort (BV/afdeling/functiegroep, override wint). */
  gereedschapsPakketId: idParam('gereedschapsPakketId').nullable().optional(),
  /** De kledingpakketten; de maten komen van de medewerker. */
  kledingPakketIds: z.array(idParam('kledingPakketId')).max(10).optional(),
  /**
   * Fase B (FO-510): geen gereedschap in deze opdracht (bijvoorbeeld alleen een
   * welkomstpakket bij een jubileum). Standaard hoort het gereedschapspakket erbij.
   */
  zonderGereedschap: z.boolean().optional(),
  /**
   * Fase B (FO-510): de PBM-koffer als complete set. Weggelaten = bij een
   * onboarding het PBM-koffer-template van de doelgroep; null = geen.
   */
  pbmPakketId: idParam('pbmPakketId').nullable().optional(),
  /**
   * Fase B (FO-510): het welkomstpakket (eigendom). Weggelaten = bij een
   * onboarding het welkomst-template van de doelgroep; null = geen.
   */
  welkomstPakketId: idParam('welkomstPakketId').nullable().optional(),
  /** Verplicht als de medewerker al eerder een welkomstpakket kreeg (FO-510 §5.2). */
  welkomstReden: z.string().trim().min(5, 'Geef een reden (minimaal 5 tekens)').max(1000).nullable().optional(),
})
export type SamenstellingAanmakenInput = z.infer<typeof SamenstellingAanmakenSchema>

/** Eén scan op de afvinklijst. `aantal` alleen voor bulk (default 1). */
export const SamenstellingScanSchema = z.object({
  token: z.string().trim().min(4, 'Scan of typ een code').max(200),
  aantal: z.number().int().min(1).max(999).optional(),
})
export type SamenstellingScanInput = z.infer<typeof SamenstellingScanSchema>

/** Handmatig afvinken, alleen met reden (bijv. label onleesbaar). */
export const SamenstelRegelAfvinkenSchema = z.object({
  aantal: z.number().int().min(1).max(999).default(1),
  /** Uniek artikel: het exemplaar dat meegaat (zonder scan gekozen). */
  uniekItemId: idParam('uniekItemId').nullable().optional(),
  reden: reden(3),
})
export type SamenstelRegelAfvinkenInput = z.infer<typeof SamenstelRegelAfvinkenSchema>

/** Een afvinking terugdraaien (verkeerd gescand). */
export const SamenstelRegelOngedaanSchema = z.object({
  uniekItemId: idParam('uniekItemId').nullable().optional(),
  aantal: z.number().int().min(1).max(999).optional(),
})
export type SamenstelRegelOngedaanInput = z.infer<typeof SamenstelRegelOngedaanSchema>

/** Een gereserveerd exemplaar blijkt defect (spec 209 §5.1). */
export const SamenstelRegelDefectSchema = z.object({
  uniekItemId: idParam('uniekItemId').nullable().optional(),
  aantal: z.number().int().min(1).max(999).optional(),
  reden: reden(3),
})
export type SamenstelRegelDefectInput = z.infer<typeof SamenstelRegelDefectSchema>

/** Uitzondering op de compleet-regel: "uitgeven zonder, volgt na" (magazijnbeheer). */
export const SamenstelRegelUitzonderingSchema = z.object({
  reden: reden(5),
})
export type SamenstelRegelUitzonderingInput = z.infer<typeof SamenstelRegelUitzonderingSchema>

/** Bestellijst doorzetten: per leverancier één bestelling. */
export const SamenstellingDoorzettenSchema = z.object({
  verwachteLeverdatum: ymd.nullable().optional(),
  /** Doorzetten terwijl dezelfde artikelen al in een open bestelling staan (nalevering). */
  bevestigDuplicaat: z.boolean().optional(),
  duplicaatReden: z.string().trim().max(1000).nullable().optional(),
})
export type SamenstellingDoorzettenInput = z.infer<typeof SamenstellingDoorzettenSchema>

/** Uitgeven: alles wat afgevinkt is in één transactie. */
export const SamenstellingUitgevenSchema = z.object({
  /** Handtekening van de medewerker (als die aanwezig is): het FO-499-formulier. */
  handtekeningOntvanger: handtekening.nullable().optional(),
  handtekeningAfgever: handtekening.nullable().optional(),
  ontvangerNaam: z.string().trim().max(120).nullable().optional(),
  /** Verwachte retourdatum voor retourplichtig gereedschap. */
  verwachteRetour: ymd.nullable().optional(),
  notitie: z.string().trim().max(2000).nullable().optional(),
})
export type SamenstellingUitgevenInput = z.infer<typeof SamenstellingUitgevenSchema>

/**
 * Fase B (FO-510 §B2/5a): n complete sets samenstellen van een set-template
 * (PBM-koffer of gereedschapsjabloon met "als set op voorraad"). Per set een
 * nieuwe koffer met eigen QR en een afvinklijst (doel DRAGER).
 */
export const SetsSamenstellenSchema = z.object({
  pakketId: idParam('pakketId'),
  aantal: z.number().int().min(1, 'Minimaal 1 set').max(20, 'Maximaal 20 sets per keer'),
  /** Het koffer-product (traceerbaar voorraadartikel): de kist wordt daar een exemplaar van. */
  dragerArtikelId: idParam('dragerArtikelId'),
  /** Magazijnlocatie waar de sets komen te liggen. */
  locatieId: idParam('locatieId').nullable().optional(),
  /** Naam op de koffer; leeg = de naam van het template. */
  naam: z.string().trim().max(120).nullable().optional(),
})
export type SetsSamenstellenInput = z.infer<typeof SetsSamenstellenSchema>

/**
 * Fase B (FO-510 §3/5b): één scan van de koffer-QR geeft koffer en volledige
 * inhoud uit. Een set die niet compleet is gaat alleen mee met een uitzondering
 * van magazijnbeheer; wat ontbreekt staat dan als "volgt na" op de koffer.
 */
export const KofferSetUitgevenSchema = z.object({
  employeeId: idParam('employeeId'),
  handtekeningOntvanger: handtekening.nullable().optional(),
  notitie: z.string().trim().max(2000).nullable().optional(),
  uitzonderingReden: reden(5).nullable().optional(),
})
export type KofferSetUitgevenInput = z.infer<typeof KofferSetUitgevenSchema>

/** De staat van één inhoudsregel bij de inname van een koffer-set. */
export const KOFFER_INNAME_STATEN = ['AANWEZIG', 'ONTBREEKT', 'DEFECT'] as const
export type KofferInnameStaat = (typeof KOFFER_INNAME_STATEN)[number]
export const KOFFER_INNAME_STAAT_LABELS: Record<KofferInnameStaat, string> = {
  AANWEZIG: 'Aanwezig',
  ONTBREEKT: 'Ontbreekt',
  DEFECT: 'Defect',
}

/**
 * Fase B (FO-510 §3/5c): één scan van de koffer-QR start de inname met
 * compleetheidscontrole tegen de uitgegeven inhoud. Elke regel krijgt een staat;
 * een afwijking zet de set op "in samenstelling" met een bijvul-afvinklijst.
 */
export const KofferSetInnemenSchema = z.object({
  locatieId: idParam('locatieId'),
  regels: z
    .array(
      z.object({
        inhoudRegelId: idParam('inhoudRegelId'),
        staat: z.enum(KOFFER_INNAME_STATEN),
        opmerking: z.string().trim().max(500).nullable().optional(),
        /** Foto's (client-UUID's, eerst geupload via POST /middelen/fotos); verplicht bij DEFECT. */
        fotoIds: z.array(MiddelFotoIdSchema).max(5).optional(),
      }),
    )
    .max(500),
  notitie: z.string().trim().max(2000).nullable().optional(),
})
export type KofferSetInnemenInput = z.infer<typeof KofferSetInnemenSchema>

export const SamenstellingAnnulerenSchema = z.object({ reden: reden(3) })
export type SamenstellingAnnulerenInput = z.infer<typeof SamenstellingAnnulerenSchema>

export const SamenstellingLijstQuerySchema = z.object({
  status: z.enum(SAMENSTELLING_STATUSSEN).optional(),
  /** Alleen open opdrachten (niet uitgegeven of geannuleerd). Default aan. */
  alleenOpen: queryBoolean().default(true),
  zoek: z.string().trim().max(100).optional(),
  employeeId: idParam('employeeId').optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type SamenstellingLijstQuery = z.infer<typeof SamenstellingLijstQuerySchema>

/** Polling voor gezamenlijk scannen: alleen een nieuwe versie terugsturen. */
export const SamenstellingDetailQuerySchema = z.object({
  versie: z.string().trim().max(64).optional(),
})

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface SamenstellingRegelDTO {
  id: string
  deel: SamenstelRegelDeelDTO
  status: SamenstelRegelStatusDTO
  kleur: SamenstelRegelKleurDTO
  omschrijving: string
  artikelNummer: string | null
  maat: string | null
  traceerbaar: boolean
  aantal: number
  aantalGereserveerd: number
  aantalBesteld: number
  aantalGescand: number
  aantalUitgegeven: number
  /** Live: vrije voorraad van het artikel (los van de reserveringen van deze opdracht). */
  vrijeVoorraad: number
  /** Waar het ligt, voor de looproute (magazijn + vak), of null. */
  locatie: string | null
  vaklocatie: string | null
  /** De gescande exemplaren met hun nummer (assetTag/serienummer). */
  gescandeItems: Array<{ id: string; code: string | null }>
  geblokkeerdReden: string | null
  /** Link naar de maten van de medewerker bij een maatblokkade. */
  matenLink: string | null
  handmatigReden: string | null
  uitzonderingReden: string | null
  uitzonderingDoorNaam: string | null
  leverancierNaam: string | null
  verwachteLeverdatum: string | null
  /** Kleding: de pakketbestelling en of die nog verwerkt moet worden. */
  uitrustingBestellingId: string | null
  bestellingStatus: string | null
  /** Fase B (FO-510) - set-regel: het template, de gereserveerde of gescande set en wat er vrij ligt. */
  setPakketNaam: string | null
  setLabel: string | null
  setsBeschikbaar: number | null
  /** Het template staat deels uitgeven toe (blijft open zonder uitzondering). */
  deelsToegestaan: boolean
  /** Welkomstpakket: eigendom van de medewerker (geen bruikleen, geen inname). */
  eigendom: boolean
}

export interface SamenstellingVoortgangDTO {
  gescand: number
  totaal: number
  stuksGescand: number
  stuksTotaal: number
  label: string
}

export interface SamenstellingTellersDTO {
  teBestellen: number
  besteld: number
  inMagazijn: number
  gescand: number
  uitgegeven: number
  geblokkeerd: number
  wachtOpGoedkeuring: number
  volgtNa: number
}

export interface SamenstellingDetailDTO {
  id: string
  nummer: string
  qrToken: string
  doel: SamenstellingDoelDTO
  status: SamenstellingStatusDTO
  employeeId: string | null
  medewerkerNaam: string | null
  onboardingId: string | null
  dragerId: string | null
  dragerLabel: string | null
  pakketNaam: string | null
  startdatum: string | null
  verwachteCompleetOp: string | null
  risico: SamenstelRisicoDTO
  gepauzeerd: boolean
  gepauzeerdDoorNaam: string | null
  gepauzeerdOp: string | null
  uitgegevenOp: string | null
  uitgegevenDoorNaam: string | null
  gereedschapsUitgifteId: string | null
  bevestigingDocumentId: string | null
  voortgang: SamenstellingVoortgangDTO
  tellers: SamenstellingTellersDTO
  /** De compleet-regel: mag er nu uitgegeven worden, en zo niet waarom. */
  magUitgeven: boolean
  uitgifteBlokkade: string | null
  /** Wat er bij uitgeven meegaat en wat open blijft (kleding volgt). */
  aantalUitTeGeven: number
  aantalBlijftOpen: number
  /** Bestellijst: regels die nog doorgezet moeten worden. */
  aantalTeBestellen: number
  /** Kleding die eerst verwerkt moet worden (bestelling goedgekeurd, nog niet verwerkt). */
  kledingTeVerwerken: Array<{ bestellingId: string; aantalRegels: number }>
  /** Versie voor het pollen: wijzigt bij elke scan of mutatie. */
  versie: string
  regels: SamenstellingRegelDTO[]
  /** Laatste gebeurtenissen (nieuwste eerst), voor wie samen scant. */
  recent: Array<{ id: string; actie: string; notitie: string | null; actorNaam: string | null; moment: string }>
  /** Mag deze gebruiker de uitzondering zetten (magazijnbeheer)? */
  magUitzondering: boolean
  /** Fase B (FO-510) - doel DRAGER: de koffer die als set gevuld wordt. */
  set: {
    dragerId: string
    uniekItemId: string
    nummer: string | null
    reeks: DragerReeks
    setStatus: DragerSetStatus | null
    compleetheidPct: number | null
    pakketType: PakketType | null
  } | null
}

export interface SamenstellingLijstRowDTO {
  id: string
  nummer: string
  status: SamenstellingStatusDTO
  doel: SamenstellingDoelDTO
  employeeId: string | null
  medewerkerNaam: string | null
  dragerLabel: string | null
  startdatum: string | null
  verwachteCompleetOp: string | null
  risico: SamenstelRisicoDTO
  gepauzeerd: boolean
  voortgang: SamenstellingVoortgangDTO
  tellers: SamenstellingTellersDTO
  laatsteActiviteitOp: string
}

/** GET /me/magazijn-toegang — welke magazijnfuncties de portal toont (server-autoritatief). */
export interface MagazijnToegangDTO {
  /** Samenstel-opdrachten zien (VOORRAAD, UITRUSTING of ONBOARDING lezen). */
  samenstellingenZien: boolean
  /** Scannen, afvinken en uitgeven (VOORRAAD bewerken). */
  samenstellen: boolean
  /** Fase C (FO-516): innamelijsten openen, afvinken en afsluiten (doelgroep inname-bevoegd). */
  innemen: boolean
}

/** Samenvatting voor de onboarding-cockpit. */
export interface SamenstellingSamenvattingDTO {
  id: string
  nummer: string
  status: SamenstellingStatusDTO
  tellers: SamenstellingTellersDTO
  voortgang: SamenstellingVoortgangDTO
  startdatum: string | null
  verwachteCompleetOp: string | null
  risico: SamenstelRisicoDTO
}

export interface SamenstellingScanResultaatDTO {
  ok: boolean
  /** Bij een weigering: de code (voor geluid/trilling en tests) en de melding. */
  code: string | null
  melding: string
  regelId: string | null
  detail: SamenstellingDetailDTO
}

export interface SamenstellingDoorzetResultaatDTO {
  /** Eén bestelling per leverancier (gereedschap). */
  bestellingen: Array<{ bestellingId: string; leverancierNaam: string; aantalRegels: number; verzonden: boolean; reden: string | null }>
  /** Regels die al in een open bestelling stonden (niet opnieuw besteld). */
  overgeslagen: number
  /** Kleding die eerst via het verwerkscherm moet (credits en leverwijze lopen daar). */
  kledingTeVerwerken: Array<{ bestellingId: string; aantalRegels: number }>
  waarschuwingen: string[]
  detail: SamenstellingDetailDTO
}

export interface SamenstellingUitgifteResultaatDTO {
  gereedschapsUitgifteId: string | null
  aantalUitgegeven: number
  /** Regels die open blijven (kleding volgt, of uitzondering "volgt na"). */
  openRegels: number
  bevestigingDocumentId: string | null
  waarschuwingen: string[]
  detail: SamenstellingDetailDTO
}

// ─── Fase B (FO-510): koffer als complete set ───────────────────────────────

export interface KofferSetInhoudDTO {
  /** DragerInhoudRegel.id. */
  id: string
  omschrijving: string
  aantal: number
  /** Status van de inhoudsregel (AANWEZIG, ONTBREEKT, KAPOT, ...). */
  status: string
  traceerbaar: boolean
  uniekItemId: string | null
  /** Nummer van het exemplaar (assetTag of serienummer). */
  code: string | null
}

export interface KofferSetDTO {
  dragerId: string
  /** Het exemplaar van de kist zelf (draagt de QR). */
  uniekItemId: string
  nummer: string | null
  naam: string
  reeks: DragerReeks
  setStatus: DragerSetStatus | null
  pakketId: string | null
  pakketNaam: string | null
  pakketType: PakketType | null
  compleetheidPct: number | null
  /** Status van het kist-exemplaar (OP_VOORRAAD, UITGEGEVEN, ...). */
  status: string
  houder: { employeeId: string; naam: string } | null
  inhoud: KofferSetInhoudDTO[]
  /** De lopende afvinklijst om de set (bij) te vullen, als die er is. */
  bijvulOpdrachtId: string | null
  /** Mag de ingelogde gebruiker de set uitgeven of innemen (VOORRAAD bewerken)? */
  magBeheren: boolean
  /**
   * Open afvinklijsten van medewerkers die op een set van dit template wachten
   * (alleen bij een complete set op voorraad). Uitgeven = de koffer daar scannen.
   */
  wachtend: Array<{ samenstellingId: string; nummer: string; medewerkerNaam: string | null; startdatum: string | null }>
}

export interface KofferSetUitgifteResultaatDTO {
  gereedschapsUitgifteId: string
  set: KofferSetDTO
  waarschuwingen: string[]
}

export interface KofferSetInnameResultaatDTO {
  set: KofferSetDTO
  /** Regels die ontbraken of defect waren. */
  afwijkingen: number
  bijvulOpdrachtId: string | null
  waarschuwingen: string[]
}

export interface SetsSamenstellenResultaatDTO {
  opdrachten: Array<{ id: string; nummer: string; dragerId: string; dragerNummer: string | null; status: SamenstellingStatusDTO }>
}

/** Voorraadbeeld per set-template (FO-510 §4/5d), met het set-advies. */
export interface SetTemplateOverzichtDTO {
  pakketId: string
  naam: string
  type: PakketType
  minimumSets: number
  compleet: number
  inSamenstelling: number
  uitgegeven: number
  inControle: number
  /** Nieuwe medewerkers die op een set wachten (set-regel zonder set). */
  wachtend: number
  /** Advies: zoveel sets samenstellen (minimum plus wachtenden min compleet). */
  advies: number
  /** Per artikel: nodig per set en vrij op voorraad (voor het advies). */
  benodigd: Array<{ voorraadArtikelId: string; naam: string; perSet: number; vrij: number }>
}

/** Fout-codes die de API als 409/422 teruggeeft (inline te tonen). */
export const SAMENSTELLING_FOUT_CODES = [
  'PAKKET_NIET_COMPLEET',
  'NIETS_TE_UITGEVEN',
  'OPDRACHT_NIET_OPEN',
  'OPDRACHT_BESTAAT_AL',
  'GEEN_PAKKET',
  'REGEL_NIET_GEVONDEN',
  'UITZONDERING_NIET_TOEGESTAAN',
  'WELKOMST_AL_ONTVANGEN',
  'SET_NIET_COMPLEET',
  'GEEN_SET_TEMPLATE',
  'MERCHANDISE_IS_EIGENDOM',
] as const
export type SamenstellingFoutCode = (typeof SAMENSTELLING_FOUT_CODES)[number]
